import {useParams, useSearchParams} from "react-router-dom";
import { useEffect, useState } from "react";
import { API } from "../lib/axios";
import moment from "moment";
import {Card, CardActions, CardHeader} from "../components/layouts/card-components.tsx";
import {useTranslation} from "react-i18next";
import {generatePeriods, Period} from "../lib/periods";
import InputSelect from "../components/input-select.tsx";
import {TableLoader} from "../components/table-loader.tsx";
import OverviewTable from "../components/overview-table.tsx";

type MetricData = {
    period: string;
    metric_value: string;
};

type ProductMetric = {
    product_code_id: string;
    metrics: MetricData[];
};

export default function ProductMetricReport() {
    const param = useParams();
    const [params] = useSearchParams();
    const productCode = param.product;
    const metric = params.get("metric") || "revenue";
    const [period, setPeriod] = useState(params.get("period") || moment().format("MM-YYYY"));
    const [periods, setPeriods] = useState<Period[]>([]);
    const [loading, setLoading] = useState(false);
    const {t} = useTranslation();

    const [data, setData] = useState<ProductMetric[]>([]);

    const downloadCSV = () => {
        if (transformedData.length === 0) return;

        const headers = Object.keys(transformedData[0]).join(',');
        const rows = transformedData.map(row =>
            Object.values(row).map(value => `"${value}"`).join(',')
        ).join('\n');

        const csvContent = `${headers}\n${rows}`;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');

        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `product_report_${productCode}_${metric}_${period}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    useEffect(() => {
        setPeriods(generatePeriods());
    }, []);

    useEffect(() => {
        setLoading(true);
        API.get(`/network?year=${period.slice(0, 4)}&month=${period.slice(5)}&report=metric_product&product_code_id=${productCode}&metric=${metric}`)
            .then(res => setData(res.data.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [productCode, metric, period]);


    const transformedData = data.map((item) => ({
        product_code_id: item.product_code_id,
        [metric]: parseFloat(item.metrics[0]?.metric_value || "0")
    }));

    return (
        <div className="z2 app_page_layout support_wide_screens">
            <Card>
                <CardHeader
                    title={`${t("product_report.title.metrics_report")} ${productCode ?? ""}`}
                    iconClass={"icon-dashboard-2"}
                />
                <div className="card_form">
                    <InputSelect
                        name="period"
                        label={t("report.placeholder.choose_period")}
                        value={period}
                        options={periods}
                        optionsText="name"
                        optionsValue="id"
                        required
                        onChange={(value) => setPeriod(value)}
                    />
                </div>
                {loading ? (
                    <TableLoader />
                ) : (
                    <OverviewTable data={transformedData} period={period} />
                )}
                <CardActions>
                    <button
                        onClick={downloadCSV}
                        className="btn btn-primary"
                        disabled={loading || transformedData.length === 0}
                    >
                        {t("download_csv") || "Download CSV"}
                    </button>
                </CardActions>
            </Card>
        </div>
    );
}
